import React from 'react';
import { motion } from 'framer-motion';

const RuralWomenEmpowerment = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-neutral-900 to-black text-white py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gradient">Rural Women Empowerment Program</h1>
          <p className="text-neutral-400 mb-12">Annual Report 2024-2025</p>

          <div className="space-y-8 text-neutral-300">
            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Program Overview</h2>
                <p className="leading-relaxed mb-4">
                  The Rural Women Empowerment Program (RWE) addresses critical issues faced by marginalized women in Budiganga rural municipality by providing comprehensive support across multiple domains.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  {[
                    "Healthcare",
                    "Economic Opportunities",
                    "Economic Independence",
                    "Improved Livelihoods",
                    "Enhanced Education",
                    "Social Empowerment",
                    "Food Security"
                  ].map((area, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-lg">
                      <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-white font-semibold">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Program Impact</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-amber-400 mb-2">1,673</div>
                    <div className="text-neutral-300">Direct Beneficiaries</div>
                    <div className="text-sm text-neutral-400 mt-2">In the first year</div>
                  </div>
                  <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-amber-400 mb-2">5,019</div>
                    <div className="text-neutral-300">Indirect Beneficiaries</div>
                    <div className="text-sm text-neutral-400 mt-2">Community-wide impact</div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">First Year Activities</h2>
                <div className="space-y-6">
                  {[
                    {
                      title: "Coordination Meeting",
                      description: "Stakeholder meetings to ensure program alignment and community engagement",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Literacy Programs",
                      description: "Non-Formal Classes, Coaching Classes in Formal Schools, Child Clubs, School Materials Support, and School Wall Painting",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Capacity Building",
                      description: "Health and Reproductive Empowerment, Economic Empowerment, Orientation on Child Marriage, Mental Health and Awareness",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Livelihood Support",
                      description: "Organic Vegetable Farming and Vendor Food Shop initiatives to promote economic independence",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Skills Training",
                      description: "Jhokra Jute and Nepali Bateko Dhup making training (incense making) for income generation",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Essential Distributions",
                      description: "Ceramic Water Filters, Mosquito Nets, and Goat Distribution to improve health and livelihoods",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
                        </svg>
                      )
                    }
                  ].map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      className="flex items-start space-x-4 p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl hover:scale-105 transition-transform duration-300"
                    >
                      <div className="text-amber-400 mt-1">{activity.icon}</div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{activity.title}</h3>
                        <p className="text-neutral-300">{activity.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Non-Formal Education Success Story</h2>
                <p className="leading-relaxed mb-6">
                  One of the successful activities under the Rural Women Empowerment Program was the Non-Formal Education initiative, which followed a participatory approach ensuring active engagement and ownership among learners.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-amber-400 mb-2">148</div>
                    <div className="text-neutral-300">Total Women Participants</div>
                    <div className="text-sm text-neutral-400 mt-2">Across four groups</div>
                  </div>
                  <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-amber-400 mb-2">94</div>
                    <div className="text-neutral-300">Women Surveyed</div>
                    <div className="text-sm text-neutral-400 mt-2">In non-formal education assessment</div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Program Outcomes</h2>
                <div className="space-y-6">
                  {[
                    {
                      number: "1",
                      title: "Academic Skills",
                      description: "Enhanced literacy and numeracy skills",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                      )
                    },
                    {
                      number: "2",
                      title: "Economic Opportunities",
                      description: "Acquired skills in modern agriculture, marketing, and cooperative involvement",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                      )
                    },
                    {
                      number: "3",
                      title: "Self-Confidence",
                      description: "Increased confidence and decision-making abilities",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                        </svg>
                      )
                    },
                    {
                      number: "4",
                      title: "Skill Development",
                      description: "Gained life, work, and communication skills",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                        </svg>
                      )
                    },
                    {
                      number: "5",
                      title: "Social Awareness",
                      description: "Greater understanding of social roles and responsibilities",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                      )
                    }
                  ].map((outcome, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      className="flex items-start space-x-4 p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl hover:scale-105 transition-transform duration-300"
                    >
                      <div className="text-amber-400 mt-1">{outcome.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="text-amber-400 font-bold text-lg">{outcome.number}.</div>
                          <h3 className="text-xl font-bold text-white">{outcome.title}</h3>
                        </div>
                        <p className="text-neutral-300">{outcome.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Program Reports</h2>
                <p className="leading-relaxed mb-6">
                  Access comprehensive reports detailing our program activities, impact assessments, and outcomes.
                </p>
                <div className="space-y-4">
                  <a
                    href="/images/Annual Report -Rural Women Empowerment 2024-2025.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl hover:scale-105 transition-transform duration-300"
                  >
                    <div className="flex items-center space-x-4">
                      <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <div>
                        <h3 className="text-lg font-bold text-white">Annual Report 2024-2025</h3>
                        <p className="text-sm text-neutral-400">Complete program overview and impact assessment</p>
                      </div>
                    </div>
                    <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                    </svg>
                  </a>
                  <a
                    href="/images/Non Formal education- Survey pre-test Post-test report (1).docx.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl hover:scale-105 transition-transform duration-300"
                  >
                    <div className="flex items-center space-x-4">
                      <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <div>
                        <h3 className="text-lg font-bold text-white">Non-Formal Education Survey Report</h3>
                        <p className="text-sm text-neutral-400">Pre-test and post-test survey results</p>
                      </div>
                    </div>
                    <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                    </svg>
                  </a>
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">Support This Program</h2>
                <p className="text-neutral-300 mb-6">
                  Your donation helps empower rural women in Nepal through education and skills training.
                </p>
                <a
                  href="/#donate"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/25"
                >
                  Donate Now
                </a>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RuralWomenEmpowerment;

